// Audio playback (rt.playAudio). On Tizen, <audio>, <video> and webapis.avplay hand audio off to the
// platform player; in this app the events (playing → ended) fire but nothing comes out of the speakers.
// Web Audio decodes inside Chromium and plays through its own output: the only path that's audible on
// the TV (measured with the audio-lab module, docs/tizen_permissions_guide.md 2.2.1).
import type { AudioPlayback } from '@frame/sdk/tv';

const CACHE = 8;

let ctx: AudioContext | null = null;
const buffers = new Map<string, Promise<AudioBuffer>>();
/** Finish callbacks of the sounds playing (onended doesn't fire once the context is closed). */
const playing = new Set<() => void>();

function context() {
  if (!ctx) {
    const C = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!C) throw new Error('Web Audio is not available in this browser');
    ctx = new C() as AudioContext;
  }
  return ctx;
}

/**
 * The autoplay policy may keep the AudioContext suspended until a user gesture.
 * The runtime calls this on every key press, so sounds triggered from the phone (with no key press) can play too.
 */
export function unlockAudio() {
  try {
    const a = context();
    if (a.state === 'suspended') a.resume().catch(() => {});
  } catch {}
}

/**
 * Release the audio output: when another app is opened or the launcher goes to the background.
 * An open AudioContext (44.1 kHz) holds on to the TV's audio output; audio from apps like YouTube
 * got mixed with it and distorted. The next key press or sound opens a new context
 * (decoded sounds are context-independent and stay cached).
 */
export function releaseAudio() {
  const a = ctx;
  if (!a) return;
  ctx = null;
  playing.forEach((finish) => finish());
  playing.clear();
  a.close().catch(() => {});
}

function load(url: string) {
  let p = buffers.get(url);
  if (!p) {
    const a = context();
    p = fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error(`failed to download audio: HTTP ${r.status}`);
        return r.arrayBuffer();
      })
      .then((data) => new Promise<AudioBuffer>((res, rej) => a.decodeAudioData(data, res, (e) => rej(e || new Error('failed to decode audio')))));
    buffers.set(url, p);
    p.catch(() => buffers.delete(url));
    while (buffers.size > CACHE) buffers.delete(buffers.keys().next().value as string);
  }
  return p;
}

export function preloadAudio(url: string) {
  try {
    load(url).catch(() => {});
  } catch {}
}

export function playAudio(url: string, opts: { volume?: number } = {}): AudioPlayback {
  let stopped = false;
  let src: AudioBufferSourceNode | null = null;
  let finish = () => {};
  const done = new Promise<void>((resolve, reject) => {
    finish = resolve;
    playing.add(resolve);
    const a = context();
    unlockAudio();
    load(url).then((buffer) => {
      // Stopped, or the context was released in the meantime (another app was opened).
      if (stopped || a !== ctx) {
        playing.delete(resolve);
        return resolve();
      }
      const gain = a.createGain();
      gain.gain.value = opts.volume ?? 1;
      gain.connect(a.destination);
      src = a.createBufferSource();
      src.buffer = buffer;
      src.connect(gain);
      src.onended = () => {
        gain.disconnect();
        playing.delete(resolve);
        resolve();
      };
      src.start();
    }, reject);
  });
  return {
    done,
    stop() {
      stopped = true;
      try {
        src?.stop();
      } catch {}
      playing.delete(finish);
      finish();
    },
  };
}
